import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Calculator, Save, Download, FileText, FolderOpen, FileUp, FileDown, Loader2, ShieldCheck, AlertTriangle, AlertCircle, Info, CheckCircle2, Check, X, MapPin, Lock, Unlock, Send, Trash2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { useToast } from "../hooks/use-toast";
import EditableRows from "../components/tax/EditableRows";
import DisallowableRows from "../components/tax/DisallowableRows";
import AssetRows from "../components/tax/AssetRows";
import ResultsPanel from "../components/tax/ResultsPanel";
import { calculateTax, saveComputation, getComputation, getCompany, importComprehensiveIncome, reviewTaxComputation, listComputations, downloadCapitalAllowanceTemplate, importCapitalAllowance, exportCapitalAllowance, submitComputation, unlockComputation, updateComputation } from "../lib/api";
import { downloadFullReportPdf } from "../lib/pdf";
import { useAuth } from "../context/AuthContext";
import ReportPreview from "../components/tax/ReportPreview";
import { fmtRM, fmtRM0 } from "../lib/taxConfig";
import {
  YA_OPTIONS, defaultOtherIncome, defaultDisallowables,
  defaultSpecialDeductions, defaultAssets, SPECIAL_CATEGORIES,
} from "../lib/taxConfig";

const sum = (rows, k = "amount") => rows.reduce((s, r) => s + Number(r[k] || 0), 0);

const NumField = ({ label, hint, value, onChange }) => (
  <div className="flex items-center justify-between border border-gray-200 rounded px-3 py-2">
    <div>
      <Label className="mb-0">{label}</Label>
      {hint && <p className="text-xs text-gray-500">{hint}</p>}
    </div>
    <Input type="number" value={value} onChange={(e) => onChange(e.target.value)} className="w-40 text-right font-mono h-8" />
  </div>
);

const CalcField = ({ label, value, tone = "gray", testid }) => {
  const tones = {
    gray: "border-gray-200 bg-gray-50 text-gray-800",
    green: "border-green-200 bg-green-50 text-green-800",
    amber: "border-amber-200 bg-amber-50 text-amber-800",
    blue: "border-blue-200 bg-blue-50 text-[#1877c9]",
  };
  return (
    <div className={`rounded px-3 py-2 border ${tones[tone]}`}>
      <div className="text-xs font-medium mb-1 leading-tight">{label}</div>
      <div className="font-mono font-semibold text-sm" data-testid={testid}>{fmtRM0(value || 0)}</div>
    </div>
  );
};

const TaxComputation = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast } = useToast();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [searchParams] = useSearchParams();
  const loadId = searchParams.get("load");

  // Submit / lock workflow
  const [compId, setCompId] = useState(null);
  const [locked, setLocked] = useState(false);
  const [submitInfo, setSubmitInfo] = useState(null);
  const [confirmMode, setConfirmMode] = useState(null); // "submit" | "unlock"
  const [confirmEmail, setConfirmEmail] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [confirmErr, setConfirmErr] = useState("");

  const [ya, setYa] = useState("2025");
  const [isSME, setIsSME] = useState(true);
  const [header, setHeader] = useState({ companyName: "", taxFileNo: "", coNo: "", bCode: "", basisFrom: "", basisTo: "", refNo: "" });

  const [netProfit, setNetProfit] = useState(0);
  const [otherIncome, setOtherIncome] = useState(defaultOtherIncome());
  const [otherStatutoryIncome, setOtherStatutoryIncome] = useState(defaultOtherIncome());
  const [interestRestriction, setInterestRestriction] = useState(0);
  const [disallowables, setDisallowables] = useState(defaultDisallowables());
  const [specialDeductions, setSpecialDeductions] = useState(defaultSpecialDeductions());
  const [assets, setAssets] = useState(defaultAssets());
  const [caImporting, setCaImporting] = useState(false);
  const [clearCaOpen, setClearCaOpen] = useState(false);
  const caFileRef = useRef(null);

  const handleClearCa = () => {
    setAssets(defaultAssets());
    setClearCaOpen(false);
    toast({ title: "Capital allowance cleared", description: "All imported asset rows were removed." });
  };

  const handleCaTemplate = async () => {
    try { await downloadCapitalAllowanceTemplate(); }
    catch (e) { toast({ title: "Could not download template.", variant: "destructive" }); }
  };

  const [caExporting, setCaExporting] = useState(false);
  const handleCaExport = async () => {
    if (!assets?.length) {
      toast({ title: "Nothing to export", description: "Add or import capital-allowance assets first.", variant: "destructive" });
      return;
    }
    setCaExporting(true);
    try {
      await exportCapitalAllowance(assets, header.companyName);
      toast({ title: "Capital allowance exported", description: "Your current schedule was downloaded as Excel." });
    } catch (e) {
      toast({ title: "Could not export capital allowance.", variant: "destructive" });
    } finally {
      setCaExporting(false);
    }
  };

  const handleCaImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCaImporting(true);
    try {
      const res = await importCapitalAllowance(file);
      if (res.assets?.length) {
        setAssets(res.assets);
        toast({ title: `Imported ${res.count} asset${res.count === 1 ? "" : "s"}`, description: "Capital allowance schedule updated." });
      } else {
        toast({ title: "No rows found", description: "The template appears empty.", variant: "destructive" });
      }
    } catch (err) {
      toast({ title: err?.response?.data?.detail || "Import failed.", variant: "destructive" });
    } finally {
      setCaImporting(false);
      if (caFileRef.current) caFileRef.current.value = "";
    }
  };
  const [capitalAllowanceBf, setCapitalAllowanceBf] = useState(0);
  const [balancingCharge, setBalancingCharge] = useState(0);
  const [unabsorbedLossBf, setUnabsorbedLossBf] = useState(0);
  const [section110, setSection110] = useState(0);
  const [section132relief, setSection132relief] = useState(0);
  const [cp204Paid, setCp204Paid] = useState(0);

  const [results, setResults] = useState(null);
  const [saving, setSaving] = useState(false);
  const [gateChecked, setGateChecked] = useState(false);

  // Import from Comprehensive Income
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importBusy, setImportBusy] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // AI tax-expert review
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewBusy, setReviewBusy] = useState(false);
  const [reviewResult, setReviewResult] = useState(null);
  const [reviewDecisions, setReviewDecisions] = useState({});
  const [dataEntry, setDataEntry] = useState({});
  const [reportOpen, setReportOpen] = useState(false);

  const runImport = async () => {
    if (!importFile) {
      toast({ title: "Please choose a PDF file.", variant: "destructive" });
      return;
    }
    setImportBusy(true);
    setImportResult(null);
    try {
      const res = await importComprehensiveIncome(importFile);
      setImportResult(res);
    } catch (e) {
      toast({ title: e?.response?.data?.detail || "Could not read the PDF.", variant: "destructive" });
    } finally {
      setImportBusy(false);
    }
  };

  const applyImport = () => {
    const r = importResult;
    if (!r) return;
    setNetProfit(r.netProfit || 0);
    setOtherIncome((r.otherIncome || []).length ? r.otherIncome.map((x) => ({ label: x.label, amount: x.amount })) : defaultOtherIncome());
    setDisallowables((r.disallowables || []).length
      ? r.disallowables.map((x, i) => ({ noteNo: i + 1, description: x.description, remark: x.remark || "", amount: x.amount, disallowed: x.disallowed || 0, treatment: "allowed", notes: [] }))
      : defaultDisallowables());
    setImportOpen(false);
    setImportFile(null);
    setImportResult(null);
    toast({ title: "Imported", description: "Figures filled from the Comprehensive Income statement." });
  };

  // Load the company record, prefill particulars, then auto-open the latest saved computation.
  useEffect(() => {
    if (loadId) { setGateChecked(true); return; }
    if (!id) {
      navigate("/company", { replace: true });
      return;
    }
    (async () => {
      try {
        const c = await getCompany(id);
        if (!c) {
          navigate("/company", { replace: true });
          return;
        }
        setDataEntry(c);
        setHeader((h) => ({
          ...h,
          companyName: c.name || h.companyName,
          coNo: c.coNo || c.regNo || h.coNo,
          taxFileNo: c.taxC || h.taxFileNo,
          bCode: c.businessCode || h.bCode,
          basisFrom: c.basisFrom || h.basisFrom,
          basisTo: c.basisTo || h.basisTo,
        }));
        if (c.ya) setYa(String(c.ya));
        // Auto-open the latest saved computation for this company (no manual restore)
        try {
          const saved = await listComputations(id);
          if (saved && saved.length) {
            navigate(`/company/${id}/tax?load=${saved[0].id}`, { replace: true });
            return;
          }
        } catch (e) { /* fall through to a fresh computation */ }
        setGateChecked(true);
      } catch (e) {
        navigate("/company", { replace: true });
      }
    })();
  }, [id, loadId]); // eslint-disable-line

  // Load a saved computation
  useEffect(() => {
    if (!loadId) return;
    (async () => {
      try {
        const comp = await getComputation(loadId);
        const i = comp.inputs || {};
        setYa(String(i.ya || "2025"));
        setIsSME(i.isSME !== undefined ? i.isSME : true);
        setHeader({
          companyName: i.companyName || "", taxFileNo: i.taxFileNo || "", coNo: i.coNo || "",
          bCode: i.bCode || "", basisFrom: i.basisFrom || "", basisTo: i.basisTo || "", refNo: i.refNo || "",
        });
        setNetProfit(i.netProfit || 0);
        setOtherIncome(i.otherIncome?.length ? i.otherIncome : defaultOtherIncome());
        setOtherStatutoryIncome(
          i.otherStatutoryIncome?.length ? i.otherStatutoryIncome
            : (i.otherIncome?.length ? i.otherIncome : defaultOtherIncome())
        );
        setInterestRestriction(i.interestRestriction || 0);
        setDisallowables(i.disallowables?.length ? i.disallowables : defaultDisallowables());
        setSpecialDeductions(i.specialDeductions?.length ? i.specialDeductions : defaultSpecialDeductions());
        setAssets(i.assets?.length ? i.assets : defaultAssets());
        setCapitalAllowanceBf(i.capitalAllowanceBf || 0);
        setBalancingCharge(i.balancingCharge || 0);
        setUnabsorbedLossBf(i.unabsorbedLossBf || 0);
        setSection110(i.section110 || 0);
        setSection132relief(i.section132relief || 0);
        setCp204Paid(i.cp204Paid || 0);
        setCompId(comp.id || loadId);
        setLocked(!!comp.locked);
        setSubmitInfo(comp.locked ? { by: comp.submittedByName, at: comp.submittedAt } : null);
        toast({ title: "Computation loaded", description: `YA ${i.ya}` });
      } catch (e) {
        toast({ title: "Could not load computation", variant: "destructive" });
      }
    })();
  }, [loadId]); // eslint-disable-line

  const buildPayload = useCallback(() => ({
    taxType: "company",
    ya: parseInt(ya, 10),
    companyId: id || null,
    isSME,
    ...header,
    netProfit: Number(netProfit || 0),
    otherIncome: otherIncome.map((r) => ({ label: r.label, amount: Number(r.amount || 0) })),
    otherStatutoryIncome: otherStatutoryIncome.map((r) => ({ label: r.label, amount: Number(r.amount || 0) })),
    interestRestriction: Number(interestRestriction || 0),
    disallowables: disallowables.map((r, i) => ({ noteNo: i + 1, description: r.description, remark: r.remark, amount: Number(r.amount || 0), disallowed: Number(r.disallowed || 0), treatment: r.treatment || "", note: r.note || "", notes: r.notes || [], payee: r.payee || "", specialCategory: r.specialCategory || "" })),
    specialDeductions: specialDeductions.map((r) => ({ label: r.label, amount: Number(r.amount || 0), category: r.category || "" })),
    assets: assets.map((a) => ({ ...a, rateIA: Number(a.rateIA || 0), rateAA: Number(a.rateAA || 0), originalCost: Number(a.originalCost || 0), qe: (a.qe === "" || a.qe === null || a.qe === undefined) ? null : (Number(a.qe) || 0), costBf: Number(a.costBf || 0), addition: Number(a.addition || 0), deletion: Number(a.deletion || 0), disposalValue: Number(a.disposalValue || 0), notional: !!a.notional, rvBf: Number(a.rvBf || 0) })),
    capitalAllowanceBf: Number(capitalAllowanceBf || 0),
    balancingCharge: Number(balancingCharge || 0),
    unabsorbedLossBf: Number(unabsorbedLossBf || 0),
    section110: Number(section110 || 0),
    section132relief: Number(section132relief || 0),
    cp204Paid: Number(cp204Paid || 0),
  }), [ya, id, isSME, header, netProfit, otherIncome, otherStatutoryIncome, interestRestriction, disallowables, specialDeductions, assets, capitalAllowanceBf, balancingCharge, unabsorbedLossBf, section110, section132relief, cp204Paid]);

  // Auto-fill special deductions from disallowed expenses tagged with a category (no double entry)
  useEffect(() => {
    const sums = {};
    disallowables.forEach((r) => {
      const tagged = (r.notes || []).filter((s) => s.treatment === "disallowed" && s.specialCategory);
      if (tagged.length) {
        tagged.forEach((s) => { sums[s.specialCategory] = (sums[s.specialCategory] || 0) + Number(s.amount || 0); });
      } else if (r.treatment === "disallowed" && r.specialCategory) {
        sums[r.specialCategory] = (sums[r.specialCategory] || 0) + Number(r.disallowed || 0);
      }
    });
    setSpecialDeductions((prev) => {
      let rows = prev.map((sd) => ({ ...sd }));
      let changed = false;
      SPECIAL_CATEGORIES.forEach((cat) => {
        const v = sums[cat.key];
        if (v === undefined) return;
        let idx = rows.findIndex((sd) => sd.category === cat.key);
        if (idx < 0) {
          // adopt an existing uncategorised row that matches the section code
          idx = rows.findIndex((sd) => !sd.category && (sd.label || "").toUpperCase().includes(cat.code.toUpperCase()));
          if (idx >= 0) { rows[idx] = { ...rows[idx], category: cat.key }; changed = true; }
        }
        if (idx >= 0) {
          if (Number(rows[idx].amount || 0) !== v) { rows[idx] = { ...rows[idx], amount: v }; changed = true; }
        } else {
          rows.push({ label: cat.sdLabel, amount: v, category: cat.key }); changed = true;
        }
      });
      // zero linked rows whose source was removed
      rows = rows.map((sd) => {
        if (sd.category && sums[sd.category] === undefined && Number(sd.amount || 0) !== 0) { changed = true; return { ...sd, amount: 0 }; }
        return sd;
      });
      return changed ? rows : prev;
    });
  }, [disallowables]);


  const timer = useRef(null);
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try { setResults(await calculateTax(buildPayload())); } catch (e) { console.error(e); }
    }, 350);
    return () => timer.current && clearTimeout(timer.current);
  }, [buildPayload]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...buildPayload(), companyName: header.companyName };
      const saved = await saveComputation(payload);
      if (saved?.id) setCompId(saved.id);
      toast({ title: "Computation saved", description: `${header.companyName || "Untitled"} — YA ${ya}` });
    } catch (e) {
      toast({ title: "Save failed", variant: "destructive" });
    } finally { setSaving(false); }
  };

  const handleDownload = () => results && downloadFullReportPdf(results);

  const openConfirm = (mode) => {
    setConfirmMode(mode);
    setConfirmEmail(user?.email || user?.loginId || "");
    setConfirmPassword("");
    setConfirmErr("");
  };

  const runConfirm = async () => {
    setConfirmBusy(true);
    setConfirmErr("");
    try {
      if (confirmMode === "submit") {
        // Persist the latest figures before locking.
        let cid = compId;
        const payload = { ...buildPayload(), companyName: header.companyName };
        if (cid) {
          await updateComputation(cid, payload);
        } else {
          const saved = await saveComputation(payload);
          cid = saved.id;
          setCompId(cid);
        }
        const res = await submitComputation(cid, confirmEmail.trim(), confirmPassword);
        setLocked(true);
        setSubmitInfo({ by: res.submittedByName, at: res.submittedAt });
        toast({ title: "Submitted & locked", description: "This computation can no longer be amended." });
        setConfirmMode(null);
        if (id) { navigate(`/company/${id}/computations`); return; }
      } else {
        const res = await unlockComputation(compId, confirmEmail.trim(), confirmPassword);
        setLocked(false);
        setSubmitInfo(null);
        toast({ title: "Unlocked", description: "You can amend this computation now." });
      }
      setConfirmMode(null);
    } catch (e) {
      setConfirmErr(e?.response?.data?.detail || "Confirmation failed. Please check your User ID and password.");
    } finally {
      setConfirmBusy(false);
    }
  };

  const applyAdjustments = () => {
    const adj = reviewResult?.adjustments || [];
    if (!adj.length) return;
    let rows = disallowables.map((r) => ({ ...r }));
    adj.forEach((a) => {
      const key = (a.description || "").toLowerCase().trim();
      const idx = rows.findIndex((r) => {
        const d = (r.description || "").toLowerCase().trim();
        return d && key && (d.includes(key) || key.includes(d));
      });
      const sub = [{ amount: Number(a.disallowed || 0), text: a.note || "Add-back per AI review", treatment: "disallowed" }];
      if (idx >= 0) {
        rows[idx] = { ...rows[idx], disallowed: Number(a.disallowed || 0), treatment: "disallowed", notes: sub, note: a.note || "" };
      } else {
        rows.push({ noteNo: rows.length + 1, description: a.description, remark: "", amount: Number(a.disallowed || 0), disallowed: Number(a.disallowed || 0), treatment: "disallowed", notes: sub, note: a.note || "" });
      }
    });
    setDisallowables(rows.map((r, i) => ({ ...r, noteNo: i + 1 })));
    setReviewOpen(false);
    toast({ title: `Applied ${adj.length} suggested add-back${adj.length > 1 ? "s" : ""}. Re-calculate to update figures.` });
  };

  const runReview = async () => {
    setReviewOpen(true);
    setReviewBusy(true);
    setReviewResult(null);
    setReviewDecisions({});
    try {
      const res = await reviewTaxComputation(buildPayload());
      setReviewResult(res);
    } catch (e) {
      toast({ title: e?.response?.data?.detail || "Review failed. Please try again.", variant: "destructive" });
      setReviewOpen(false);
    } finally {
      setReviewBusy(false);
    }
  };

  const setH = (k, v) => setHeader((h) => ({ ...h, [k]: v }));
  const card = "bg-white border border-gray-200 rounded-sm shadow-sm p-5 space-y-4";

  if (id && !loadId && !gateChecked) return null;

  return (
    <div className="max-w-[1400px] mx-auto pb-8">
      <button onClick={() => navigate("/company")} className="flex items-center gap-1 text-[#1877c9] hover:underline text-sm mb-3">
        <ArrowLeft size={16} /> Back
      </button>

      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded bg-[#1877c9] text-white flex items-center justify-center"><Calculator size={20} /></div>
          <div>
            <h1 className="text-xl font-semibold text-gray-800">Company Tax Computation</h1>
            <p className="text-sm text-gray-500">Form C — LHDN computation & printable report</p>
          </div>
        </div>
        {id && (
          <div className="flex items-center gap-2">
            <Button variant="outline" disabled={locked} onClick={() => setImportOpen(true)} className="gap-2 border-[#1877c9] text-[#1877c9] hover:bg-[#eef4fb]">
              <FileUp size={15} /> Import from Comprehensive Income
            </Button>
            <Button variant="outline" onClick={() => navigate(`/company/${id}/computations`)} className="gap-2 border-[#1877c9] text-[#1877c9] hover:bg-[#eef4fb]">
              <FolderOpen size={15} /> Saved Computations
            </Button>
          </div>
        )}
      </div>

      {locked && (
        <div className="mb-4 flex items-center justify-between gap-3 rounded-sm border border-amber-300 bg-amber-50 px-4 py-3" data-testid="locked-banner">
          <div className="flex items-center gap-2 text-amber-800 text-sm">
            <Lock size={16} />
            <span>
              <strong>Submitted & locked.</strong> This computation cannot be amended
              {submitInfo?.by ? <> — submitted by {submitInfo.by}</> : null}
              {submitInfo?.at ? <> on {new Date(submitInfo.at).toLocaleString()}</> : null}.
              {isAdmin ? " An admin can unlock it to make changes." : " Ask an admin to unlock it."}
            </span>
          </div>
          {isAdmin && (
            <Button onClick={() => openConfirm("unlock")} variant="outline" className="gap-2 h-9 border-amber-500 text-amber-700 hover:bg-amber-100 shrink-0" data-testid="unlock-btn">
              <Unlock size={15} /> Unlock
            </Button>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        <fieldset disabled={locked} className="lg:col-span-3 space-y-4 border-0 p-0 m-0 min-w-0 disabled:opacity-70">
          {/* Header info */}
          <div className={card}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Company Name</Label><Input value={header.companyName} onChange={(e) => setH("companyName", e.target.value)} className="h-9" /></div>
              <div className="space-y-1"><Label>Tax File No.</Label><Input value={header.taxFileNo} onChange={(e) => setH("taxFileNo", e.target.value)} className="h-9" /></div>
              <div className="space-y-1"><Label>Co. No.</Label><Input value={header.coNo} onChange={(e) => setH("coNo", e.target.value)} className="h-9" /></div>
              <div className="space-y-1"><Label>Business Code (B/CODE)</Label><Input value={header.bCode} onChange={(e) => setH("bCode", e.target.value)} className="h-9" /></div>
              <div className="space-y-1"><Label>Basis Period From</Label><Input value={header.basisFrom} onChange={(e) => setH("basisFrom", e.target.value)} className="h-9" placeholder="01-01-2025" /></div>
              <div className="space-y-1"><Label>Basis Period To</Label><Input value={header.basisTo} onChange={(e) => setH("basisTo", e.target.value)} className="h-9" placeholder="31-12-2025" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3 items-end">
              <div className="space-y-1">
                <Label>Year of Assessment</Label>
                <Select value={ya} onValueChange={setYa}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>{YA_OPTIONS.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between bg-[#f7f9fc] rounded px-3 py-2 h-9 mt-5">
                <span className="text-sm text-gray-700">{isSME ? "SME (15/17/24%)" : "Non-SME (24%)"}</span>
                <Switch checked={isSME} onCheckedChange={setIsSME} />
              </div>
            </div>
          </div>

          {/* Business income */}
          <div className={card}>
            <h3 className="text-sm font-semibold text-[#1877c9] uppercase tracking-wide">Business Income</h3>
            <NumField label="Net profit / (loss) as per accounts" hint="Enter a negative value for a loss" value={netProfit} onChange={setNetProfit} />
            <EditableRows title="Less: Non-business (other) income" accent="bg-[#fdf2f2] text-red-600" rows={otherIncome} onChange={setOtherIncome} total={sum(otherIncome)} />
            <NumField label="Add: Interest restriction under Sec.33(2)" value={interestRestriction} onChange={setInterestRestriction} />
            <DisallowableRows rows={disallowables} onChange={setDisallowables} />
            <EditableRows title="Less: Allowance & special deductions (S.111 / S.132 / S.157)" accent="bg-[#fdf2f2] text-red-600" rows={specialDeductions} onChange={setSpecialDeductions} total={sum(specialDeductions)} />
          </div>

          {/* Capital allowance */}
          <div className={card}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-sm font-semibold text-[#1877c9] uppercase tracking-wide">Capital Allowance</h3>
              <div className="flex items-center gap-2">
                <input
                  ref={caFileRef}
                  type="file"
                  accept=".xlsx,.xlsm"
                  className="hidden"
                  onChange={handleCaImport}
                  data-testid="ca-import-input"
                />
                <Button variant="outline" size="sm" onClick={handleCaTemplate} className="gap-1.5 h-8 border-[#1877c9] text-[#1877c9] hover:bg-[#eef4fb]" data-testid="ca-download-template-btn">
                  <Download size={14} /> Excel Template
                </Button>
                <Button variant="outline" size="sm" onClick={handleCaExport} disabled={caExporting} className="gap-1.5 h-8 border-[#1877c9] text-[#1877c9] hover:bg-[#eef4fb]" data-testid="ca-export-btn">
                  {caExporting ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />} Download Excel
                </Button>
                <Button variant="outline" size="sm" onClick={() => caFileRef.current?.click()} disabled={caImporting} className="gap-1.5 h-8 border-[#1877c9] text-[#1877c9] hover:bg-[#eef4fb]" data-testid="ca-import-btn">
                  {caImporting ? <Loader2 size={14} className="animate-spin" /> : <FileUp size={14} />} Import Template
                </Button>
                <Button variant="outline" size="sm" onClick={() => setClearCaOpen(true)} className="gap-1.5 h-8 border-red-300 text-red-600 hover:bg-red-50" data-testid="ca-clear-btn">
                  <Trash2 size={14} /> Clear CA
                </Button>
              </div>
            </div>
            <Dialog open={clearCaOpen} onOpenChange={setClearCaOpen}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Clear imported capital allowance?</DialogTitle>
                  <DialogDescription>
                    This removes all capital-allowance asset rows currently in this computation and starts fresh with an empty schedule. This cannot be undone (unless you re-import or reload a saved computation).
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setClearCaOpen(false)}>Cancel</Button>
                  <Button onClick={handleClearCa} className="bg-red-600 hover:bg-red-700" data-testid="ca-clear-confirm">Clear all</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <AssetRows rows={assets} onChange={setAssets} />
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              <div className="rounded px-3 py-2 border border-gray-200">
                <div className="text-xs font-medium text-gray-700 mb-1 leading-tight">Capital allowance b/f</div>
                <Input type="number" value={capitalAllowanceBf} onChange={(e) => setCapitalAllowanceBf(e.target.value)} className="w-full text-right font-mono h-8" data-testid="ca-bf-input" />
              </div>
              <CalcField label="Current capital allowance" value={results?.capitalAllowanceCurrent} tone="blue" testid="ca-current" />
              <CalcField label="Notional allowance" value={results?.notionalAllowance} tone="gray" testid="ca-notional" />
              <CalcField label="Balancing allowance" value={results?.balancingAllowance} tone="green" testid="ca-balancing-allowance" />
              <CalcField label="Balancing charge" value={results?.balancingChargeComputed} tone="amber" testid="ca-balancing-charge" />
              <CalcField label="ACA (claimed)" value={results?.acaClaimed} tone="blue" testid="ca-aca" />
              <CalcField label="ACA c/f (not claimed)" value={results?.acaCfNotClaimed} tone="amber" testid="ca-aca-cf" />
            </div>
            <p className="text-xs text-gray-500">Current-year capital allowance (incl. balancing allowance) is auto-computed from the asset schedule. On disposal, RE = R.V. b/f; DV &lt; RE gives a balancing allowance, DV &gt; RE a balancing charge.</p>
          </div>

          {/* Other statutory income */}
          <div className={card} data-testid="other-statutory-income-card">
            <h3 className="text-sm font-semibold text-[#1877c9] uppercase tracking-wide">Other Statutory Income</h3>
            <p className="text-xs text-gray-500 -mt-1">Statutory income added at the aggregate level (e.g. Deemed Interest Income under S.140B). This is a separate list from "Less: Non-business (other) income" above.</p>
            <EditableRows title="Add: Other statutory income" accent="bg-[#eef6ef] text-emerald-700" rows={otherStatutoryIncome} onChange={setOtherStatutoryIncome} total={sum(otherStatutoryIncome)} />
          </div>

          {/* Losses & CP204 */}
          <div className={card}>
            <h3 className="text-sm font-semibold text-[#1877c9] uppercase tracking-wide">Losses & Tax Paid</h3>
            <NumField label="Unabsorbed business losses b/f" value={unabsorbedLossBf} onChange={setUnabsorbedLossBf} />
            <NumField label="Actual tax paid via CP204 instalment" hint="Deducted to show balance payable / refund" value={cp204Paid} onChange={setCp204Paid} />
          </div>
        </fieldset>

        {/* Results */}
        <div className="lg:col-span-2">
          <div className="bg-white border border-gray-200 rounded-sm shadow-sm p-5 sticky top-20 max-h-[calc(100vh-100px)] overflow-y-auto ibizz-scroll">
            <div className="flex items-center justify-between mb-3 gap-2">
              <h3 className="font-semibold text-gray-800 shrink-0">Tax Computation</h3>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                <Button onClick={handleDownload} disabled={!results} className="gap-2 h-9 bg-[#1877c9] hover:bg-[#0f5ea3]" data-testid="generate-report-btn">
                  <FileText size={15} /> Report
                </Button>
                {locked ? (
                  <span className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-amber-300 bg-amber-50 text-amber-700 text-sm font-medium" data-testid="locked-pill">
                    <Lock size={14} /> Locked
                  </span>
                ) : (
                  <>
                    <Button onClick={handleSave} disabled={saving} variant="outline" className="gap-2 h-9 border-[#1877c9] text-[#1877c9] hover:bg-[#eef4fb]" data-testid="save-computation-btn">
                      <Save size={15} /> {saving ? "..." : "Save"}
                    </Button>
                    <Button onClick={() => openConfirm("submit")} disabled={!results} className="gap-2 h-9 bg-emerald-600 hover:bg-emerald-700" data-testid="submit-computation-btn">
                      <Send size={15} /> Submit
                    </Button>
                  </>
                )}
              </div>
            </div>
            <ResultsPanel results={results} />
          </div>
        </div>
      </div>

      {/* Submit / Unlock password confirmation */}
      <Dialog open={!!confirmMode} onOpenChange={(o) => { if (!o) setConfirmMode(null); }}>
        <DialogContent className="max-w-md" data-testid="confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {confirmMode === "unlock" ? <Unlock size={18} /> : <Lock size={18} />}
              {confirmMode === "unlock" ? "Unlock Computation" : "Submit & Lock Computation"}
            </DialogTitle>
            <DialogDescription>
              {confirmMode === "unlock"
                ? "Enter your admin User ID and password to unlock. Once unlocked the computation can be amended again."
                : "Enter your User ID and password to submit. Once submitted the computation is locked and cannot be amended (only an admin can unlock it)."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="mb-1">User ID</Label>
              <Input value={confirmEmail} onChange={(e) => setConfirmEmail(e.target.value)} placeholder="User ID / email" data-testid="confirm-email-input" />
            </div>
            <div>
              <Label className="mb-1">Password</Label>
              <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Password"
                onKeyDown={(e) => { if (e.key === "Enter" && confirmEmail && confirmPassword) runConfirm(); }}
                data-testid="confirm-password-input" />
            </div>
            {confirmErr && <p className="text-sm text-red-600" data-testid="confirm-error">{confirmErr}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmMode(null)} disabled={confirmBusy}>Cancel</Button>
            <Button
              onClick={runConfirm}
              disabled={confirmBusy || !confirmEmail || !confirmPassword}
              className={confirmMode === "unlock" ? "bg-amber-600 hover:bg-amber-700 gap-2" : "bg-emerald-600 hover:bg-emerald-700 gap-2"}
              data-testid="confirm-submit-btn"
            >
              {confirmBusy ? <Loader2 size={15} className="animate-spin" /> : (confirmMode === "unlock" ? <Unlock size={15} /> : <Send size={15} />)}
              {confirmMode === "unlock" ? "Unlock" : "Submit & Lock"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import from Comprehensive Income */}
      <Dialog open={importOpen} onOpenChange={(o) => { setImportOpen(o); if (!o) { setImportFile(null); setImportResult(null); } }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Import from Details of Comprehensive Income</DialogTitle>
            <DialogDescription>
              Upload the PDF. We read the current-year column and fill Net profit/(loss),
              Other operating income, and Administrative expenses &amp; Finance cost (as disallowable rows).
            </DialogDescription>
          </DialogHeader>

          {!importResult ? (
            <div className="space-y-4 py-2">
              <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-gray-300 rounded-lg p-8 cursor-pointer hover:border-[#1877c9]">
                <FileUp size={28} className="text-[#1877c9]" />
                <span className="text-sm text-gray-600">{importFile ? importFile.name : "Click to choose a PDF file"}</span>
                <input type="file" accept="application/pdf" className="hidden" onChange={(e) => setImportFile(e.target.files?.[0] || null)} />
              </label>
              {importBusy && (
                <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                  <Loader2 size={16} className="animate-spin" /> Reading the statement, this may take a few seconds...
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3 py-2 text-sm">
              <div className="flex items-center justify-between bg-[#f7f9fc] rounded px-3 py-2">
                <span className="text-gray-600">Net profit / (loss) before tax</span>
                <span className={`font-mono font-semibold ${importResult.netProfit < 0 ? "text-red-600" : "text-gray-800"}`}>{fmtRM(Math.abs(importResult.netProfit))}{importResult.netProfit < 0 ? " (Loss)" : ""}</span>
              </div>
              <div>
                <div className="text-xs font-semibold text-[#1877c9] uppercase mb-1">Other operating income ({importResult.otherIncome?.length || 0})</div>
                {(importResult.otherIncome || []).map((x, i) => (
                  <div key={i} className="flex justify-between border-b border-gray-100 py-1"><span className="text-gray-600">{x.label}</span><span className="font-mono">{fmtRM(x.amount)}</span></div>
                ))}
              </div>
              <div>
                <div className="text-xs font-semibold text-[#1877c9] uppercase mb-1">Disallowable expenses — admin &amp; finance ({importResult.disallowables?.length || 0})</div>
                <div className="max-h-52 overflow-y-auto ibizz-scroll">
                  {(importResult.disallowables || []).map((x, i) => (
                    <div key={i} className="flex justify-between border-b border-gray-100 py-1"><span className="text-gray-600">{x.description}</span><span className="font-mono">{fmtRM(x.amount)}</span></div>
                  ))}
                </div>
                <p className="text-xs text-gray-400 mt-1">Amount disallowed defaults to 0 — adjust each line after import.</p>
              </div>
            </div>
          )}

          <DialogFooter>
            {!importResult ? (
              <>
                <Button variant="outline" onClick={() => setImportOpen(false)}>Cancel</Button>
                <Button onClick={runImport} disabled={importBusy || !importFile} className="bg-[#1877c9] hover:bg-[#0f5ea3]">
                  {importBusy ? "Reading..." : "Read PDF"}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setImportResult(null)}>Back</Button>
                <Button onClick={applyImport} className="bg-[#1877c9] hover:bg-[#0f5ea3]">Apply to form</Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Tax-Expert Review */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="review-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldCheck size={18} className="text-emerald-600" /> Malaysian Tax Expert Review</DialogTitle>
            <DialogDescription>An AI LHDN Form C specialist reviews this computation for errors and likely mistakes.</DialogDescription>
          </DialogHeader>

          {reviewBusy ? (
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500 py-10">
              <Loader2 size={18} className="animate-spin" /> Reviewing the tax computation...
            </div>
          ) : reviewResult ? (
            <div className="space-y-3 py-1">
              <div className={`flex items-start gap-2 rounded px-3 py-2 text-sm ${reviewResult.overall === "ok" ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-800"}`} data-testid="review-summary">
                {reviewResult.overall === "ok" ? <CheckCircle2 size={18} className="mt-0.5 shrink-0" /> : <AlertTriangle size={18} className="mt-0.5 shrink-0" />}
                <span>{reviewResult.summary || (reviewResult.overall === "ok" ? "No issues found." : "Some items need your attention.")}</span>
              </div>

              {(reviewResult.findings || []).length === 0 ? (
                <p className="text-sm text-gray-500">No specific findings — the computation looks correct.</p>
              ) : (
                <>
                  {(() => {
                    const total = reviewResult.findings.length;
                    const accepted = Object.values(reviewDecisions).filter((v) => v === "accepted").length;
                    const dismissed = Object.values(reviewDecisions).filter((v) => v === "dismissed").length;
                    const pending = total - accepted - dismissed;
                    return (
                      <div className="flex items-center gap-3 text-xs font-medium" data-testid="review-decision-summary">
                        <span className="text-emerald-700">{accepted} accepted</span>
                        <span className="text-gray-400">·</span>
                        <span className="text-gray-500">{dismissed} dismissed</span>
                        <span className="text-gray-400">·</span>
                        <span className="text-amber-700">{pending} to review</span>
                      </div>
                    );
                  })()}
                  <div className="space-y-2" data-testid="review-findings">
                    {reviewResult.findings.map((f, i) => {
                      const map = {
                        error: { c: "border-red-200 bg-red-50", t: "text-red-700", Icon: AlertCircle },
                        warning: { c: "border-amber-200 bg-amber-50", t: "text-amber-700", Icon: AlertTriangle },
                        info: { c: "border-blue-200 bg-blue-50", t: "text-blue-700", Icon: Info },
                      };
                      const s = map[f.severity] || map.info;
                      const decision = reviewDecisions[i];
                      const cardCls = decision === "accepted"
                        ? "border-emerald-300 bg-emerald-50"
                        : decision === "dismissed"
                        ? "border-gray-200 bg-gray-50 opacity-60"
                        : s.c;
                      const toggle = (val) => setReviewDecisions((d) => ({ ...d, [i]: d[i] === val ? undefined : val }));
                      return (
                        <div key={i} className={`border rounded p-3 transition-colors ${cardCls}`} data-testid={`review-finding-${i}`}>
                          <div className={`flex items-center gap-2 text-sm font-semibold ${decision === "dismissed" ? "text-gray-500" : s.t}`}>
                            <s.Icon size={15} /> {f.area || f.severity.toUpperCase()}
                            {decision === "accepted" && <span className="text-[10px] uppercase tracking-wide bg-emerald-600 text-white rounded px-1.5 py-0.5">Accepted</span>}
                            {decision === "dismissed" && <span className="text-[10px] uppercase tracking-wide bg-gray-400 text-white rounded px-1.5 py-0.5">Dismissed</span>}
                            <span className="ml-auto text-[10px] uppercase tracking-wide bg-white/70 rounded px-1.5 py-0.5">{f.severity}</span>
                          </div>
                          {f.location && (
                            <div className={`flex items-center gap-1 mt-1.5 text-xs ${decision === "dismissed" ? "text-gray-400" : "text-gray-600"}`} data-testid={`review-finding-${i}-location`}>
                              <MapPin size={12} className="shrink-0" />
                              <span className="font-medium">Where:</span>
                              <span className="px-1.5 py-0.5 rounded bg-white/70 border border-black/5">{f.location}</span>
                            </div>
                          )}
                          {f.issue && <p className={`text-sm mt-1 ${decision === "dismissed" ? "text-gray-400 line-through" : "text-gray-700"}`}>{f.issue}</p>}
                          {f.suggestion && <p className={`text-sm mt-1 ${decision === "dismissed" ? "text-gray-400" : "text-gray-600"}`}><span className="font-medium">Fix:</span> {f.suggestion}</p>}
                          <div className="flex items-center gap-2 mt-2">
                            <button
                              onClick={() => toggle("accepted")}
                              data-testid={`review-accept-${i}`}
                              className={`inline-flex items-center gap-1 text-xs font-medium rounded px-2.5 py-1 border transition-colors ${decision === "accepted" ? "bg-emerald-600 text-white border-emerald-600" : "border-emerald-300 text-emerald-700 hover:bg-emerald-50"}`}
                            >
                              <Check size={13} /> Accept
                            </button>
                            <button
                              onClick={() => toggle("dismissed")}
                              data-testid={`review-dismiss-${i}`}
                              className={`inline-flex items-center gap-1 text-xs font-medium rounded px-2.5 py-1 border transition-colors ${decision === "dismissed" ? "bg-gray-500 text-white border-gray-500" : "border-gray-300 text-gray-600 hover:bg-gray-100"}`}
                            >
                              <X size={13} /> Dismiss
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
              <p className="text-xs text-gray-400">AI guidance only — verify against current LHDN rulings before filing.</p>
            </div>
          ) : null}

          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewOpen(false)}>Close</Button>
            {!reviewBusy && (reviewResult?.adjustments || []).length > 0 && (
              <Button onClick={applyAdjustments} className="bg-[#1877c9] hover:bg-[#0f5ea3]" data-testid="review-apply">
                Apply {reviewResult.adjustments.length} suggested add-back{reviewResult.adjustments.length > 1 ? "s" : ""}
              </Button>
            )}
            {!reviewBusy && <Button onClick={runReview} className="bg-emerald-600 hover:bg-emerald-700" data-testid="review-rerun">Re-review</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {reportOpen && (
        <ReportPreview r={results} onClose={() => setReportOpen(false)} onDownload={handleDownload} />
      )}
    </div>
  );
};

export default TaxComputation;
